import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLexware } from '../context/LexwareContext';

const { FiPlus, FiEdit3, FiTrash2, FiCopy, FiCheck, FiX, FiMail, FiCode, FiZap } = FiIcons;

// Verfügbare Variablen für die Personalisierung
const AVAILABLE_VARIABLES = [
  { id: '{Anrede}', label: 'Anrede', desc: 'Sehr geehrte/r... / Hallo...', color: 'bg-blue-100 text-blue-700' },
  { id: '{Vorname}', label: 'Vorname', desc: 'Nur Vorname', color: 'bg-indigo-100 text-indigo-700' },
  { id: '{Name}', label: 'Vollst. Name', desc: 'Vor- und Nachname', color: 'bg-purple-100 text-purple-700' },
  { id: '{Firma}', label: 'Firma', desc: 'Firmenname', color: 'bg-orange-100 text-orange-700' },
  { id: '{Kundennummer}', label: 'Kd-Nr.', desc: 'Kundenummer', color: 'bg-gray-100 text-gray-700' },
];

function MessageTemplates() {
  const { data, updateTemplates, logActivity } = useLexware();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [formData, setFormData] = useState({ shortName: '', content: '' });
  
  // Ref für das Textarea-Element, um den Cursor zu steuern
  const textareaRef = useRef(null);

  const templates = data.templates || [];

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({ shortName: template.shortName, content: template.content });
    } else {
      setEditingTemplate(null);
      setFormData({ shortName: '', content: '' });
    }
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!formData.shortName || !formData.content) return;
    let newTemplates;
    if (editingTemplate) {
      newTemplates = templates.map(t => t.id === editingTemplate.id ? { ...t, ...formData } : t);
      logActivity('TEMPLATE', `Vorlage "${formData.shortName}" aktualisiert`);
    } else {
      const newT = { id: 'temp-' + Date.now(), ...formData };
      newTemplates = [...templates, newT];
      logActivity('TEMPLATE', `Neue Vorlage "${formData.shortName}" erstellt`);
    }
    updateTemplates(newTemplates);
    setIsModalOpen(false);
  };

  const handleCopy = (content, id) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    logActivity('COPY', `Text aus Vorlage kopiert`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Funktion zum Einfügen der Variable an der Cursor-Position
  const insertVariable = (variableId) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const before = text.substring(0, start);
    const after = text.substring(end);

    const newContent = before + variableId + after;
    setFormData({ ...formData, content: newContent });

    // Cursor nach dem Einfügen hinter die Variable setzen
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + variableId.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nachrichtenvorlagen</h1>
          <p className="text-sm text-gray-500 mt-1">
            Absender: <span className="font-mono font-bold text-blue-600">bestellung@grundrisstausch.de</span>
          </p>
        </div>
        <button onClick={() => handleOpenModal()} className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all font-bold">
          <SafeIcon icon={FiPlus} /> Neue Vorlage
        </button>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <motion.div key={template.id} layout className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col group overflow-hidden hover:shadow-md transition-all">
            <div className="p-4 border-b bg-gray-50/50 flex justify-between items-center">
              <h3 className="font-black text-gray-900 text-sm truncate">{template.shortName}</h3>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => handleOpenModal(template)} className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"><SafeIcon icon={FiEdit3} /></button>
                <button onClick={() => { if (confirm("Löschen?")) updateTemplates(templates.filter(t => t.id !== template.id)) }} className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"><SafeIcon icon={FiTrash2} /></button>
              </div>
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-600 font-medium line-clamp-5 flex-1 border border-gray-100 mb-6 leading-relaxed">
                {template.content}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => handleCopy(template.content, template.id)} className={`py-2.5 rounded-xl text-xs font-black border transition-all flex items-center justify-center gap-2 ${copiedId === template.id ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                  <SafeIcon icon={copiedId === template.id ? FiCheck : FiCopy} /> {copiedId === template.id ? 'Kopiert' : 'Kopieren'}
                </button>
                <button onClick={() => {
                  const subject = encodeURIComponent(template.shortName);
                  const body = encodeURIComponent(template.content);
                  window.location.href = `mailto:?subject=${subject}&body=${body}`;
                }} className="py-2.5 bg-gray-900 text-white rounded-xl text-xs font-black hover:bg-black shadow-md flex items-center justify-center gap-2">
                  <SafeIcon icon={FiMail} /> Mailen
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white">
                    <SafeIcon icon={editingTemplate ? FiEdit3 : FiPlus} />
                  </div>
                  <h2 className="text-xl font-black text-gray-900">{editingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage'}</h2>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><SafeIcon icon={FiX} className="text-xl text-gray-400" /></button>
              </div>

              <div className="p-8 space-y-6 overflow-y-auto">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Betreff / Name</label>
                  <input 
                    type="text" 
                    value={formData.shortName} 
                    onChange={e => setFormData({ ...formData, shortName: e.target.value })} 
                    placeholder="E-Mail Betreff oder interner Name" 
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-blue-500 font-bold transition-all" 
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Inhalt</label>
                    <div className="flex items-center gap-1.5 text-[10px] text-blue-600 font-bold">
                       <SafeIcon icon={FiZap} className="text-xs" /> Tipp: Variablen klicken zum Einfügen
                    </div>
                  </div>

                  {/* VARIABLE PICKER BAR */}
                  <div className="flex flex-wrap gap-2 mb-3 p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                    {AVAILABLE_VARIABLES.map(variable => (
                      <button
                        key={variable.id}
                        type="button"
                        onClick={() => insertVariable(variable.id)}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-black border border-transparent hover:border-blue-300 transition-all flex flex-col items-start ${variable.color} shadow-sm active:scale-95`}
                        title={variable.desc}
                      >
                        <span>{variable.id}</span>
                        <span className="text-[9px] opacity-70 font-medium">{variable.label}</span>
                      </button>
                    ))}
                  </div>

                  <textarea 
                    ref={textareaRef}
                    rows={10} 
                    value={formData.content} 
                    onChange={e => setFormData({ ...formData, content: e.target.value })} 
                    placeholder="Schreiben Sie hier Ihre Nachricht... Nutzen Sie die Variablen oben für Personalisierung." 
                    className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm font-medium leading-relaxed transition-all resize-none" 
                  />
                </div>
              </div>

              <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                <button onClick={() => setIsModalOpen(false)} className="px-6 py-2 text-sm font-bold text-gray-400 hover:text-gray-600">Abbrechen</button>
                <button 
                  onClick={handleSave} 
                  disabled={!formData.shortName || !formData.content}
                  className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-200 hover:bg-blue-700 disabled:opacity-50 disabled:shadow-none transition-all flex items-center gap-2"
                >
                  <SafeIcon icon={FiCheck} /> Vorlage speichern
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MessageTemplates;